
import { Options, mixins } from "vue-class-component";
import { CargoController } from "@/app/ui/controllers/CargoController";
import {
  AddBagOrStt,
  DimensionPieces,
  FlightPlanForECargo,
  CalculateVolumeApiRequest,
  CalculateVolumeRequest
} from "@/data/payload/api/CargoApiRequest";
import {
  commaDecimalToDecimal,
  dateTimePayload,
  formatDate,
  parsingErrorResponse,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { gtmCargoData } from "@/app/ui/views/cargo/modules/gtm";
import { dataLayer } from "@/app/infrastructures/misc/UtilsGtm";
import {
  CargoDetailStt,
  CargoPlaneAutoData,
  DimensionPerKoliDetail,
  CargoData
} from "@/domain/entities/Cargo";
import { capitalize } from "@vue/shared";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { LocationController } from "@/app/ui/controllers/LocationController";
import OutGoingMixins from "@/app/ui/views/out-going-shipment/out-going-mixin";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import Print from "@/app/ui/views/cargo/modules/print.vue";
import firstCapitalize from "@/app/infrastructures/misc/common-library/FirstCapitalize";
import { GTMBookingCargoEvent } from "@/app/infrastructures/misc/gtm-event/custom-events/booking-cargo-event";
import { ReadyToCargoController } from "@/app/ui/controllers/ReadyToCargoController";
import { flags } from "@/feature-flags";
import { NGenController } from "@/app/ui/controllers/NGenController";
import { getTokenNGen } from "@/app/infrastructures/misc/Cookies";
import ModalCompleteCargo from "../components/modal-complete-cargo.vue";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import {
  getConfigDimensionDivider,
  handleFailedBookingNavigation,
  getConfigIsminimumVolumePerPiece
} from "../../modules/cargo-module";
import { getDataSessionStorage } from "@/app/infrastructures/misc/Utils";
import { useCargo } from "../composables/cargo";

const { checkEligible } = useCargo();

/* eslint-disable @typescript-eslint/camelcase */

@Options({
  emits: ["leave-page", "onCheckCutOffEditRTC"],
  components: {
    Print,
    ModalCompleteCargo
  }
})
export default class CargoProcessCargo extends mixins(OutGoingMixins) {
  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  mounted() {
    CargoController.setOpenSuccess(false);
    CargoController.setFailed(false);
    CargoController.setResetErrorInputStepThree();
  }

  get cutOffTimeExceeded() {
    const {
      cutOffTime,
      cargoType,
      nextFlightsData
    } = ReadyToCargoController.readyToCargoDetail;

    const isMoreThanCutOffTime: boolean = !cutOffTime && cargoType === "plane";

    const cutOffTimeExceededWithoutNextConfig: boolean =
      isMoreThanCutOffTime && !nextFlightsData?.[0]?.departureHour;

    const cutOffTimeExceededWithNextConfig: boolean =
      isMoreThanCutOffTime && !!nextFlightsData?.[0]?.departureHour;

    return {
      isMoreThanCutOffTime,
      cutOffTimeExceededWithoutNextConfig,
      cutOffTimeExceededWithNextConfig
    };
  }

  get formBooking() {
    return CargoController.form;
  }

  get progress(): number {
    return CargoController.progressValue;
  }

  get cotHour() {
    return this.formBooking.flightsData[0]?.departureHour;
  }

  get nextCotHour() {
    return this.formBooking.nextFlightsData[0]?.departureHour;
  }

  get isShowModalComplete() {
    return CargoController.showModalComplete;
  }

  get isFormBookingValid() {
    return (
      this.formBooking.estDepartureDate &&
      (this.isPlaneAuto
        ? this.formBooking.airportOrigin.airportCode &&
          (this.formBooking.airportDestination.airportCode ||
            this.formBooking.destinationCityCode)
        : true) &&
      this.validationFormBooking &&
      this.validationCargoAndVehicleNumber &&
      this.validDispute &&
      this.validBagDispute
    );
  }

  get validationCargoAndVehicleNumber() {
    const cargoNumber =
      this.formBooking.cargoTypeStatus !== "auto" &&
      this.formBooking.cargoType !== "truck" ? this.formBooking.cargoNumber : true;
    const vehicleNumber = this.formBooking.cargoTypeStatus !== "auto" ? this.formBooking.vehicleNumber : true;
    return cargoNumber && vehicleNumber;
  }

  get validationFormBooking() {
    return (
      this.formBooking.cargoProduct &&
      this.formBooking.actualDimension &&
      this.formBooking.actualTotalCargoGrossWeight &&
      this.formBooking.actualTotalCargoVolumeWeight &&
      !this.errorInputStepThree &&
      !this.isErrorDiffPiecesActualThanDimensionPieces &&
      this.validationErrorWeightAndColi &&
      this.isUploadPhotoAdjustWeightValid
    );
  }
  get validDispute() {
    return !CargoController.isExceedMaxDisputeWeight;
  }
  get validBagDispute() {
    return !CargoController.isBagOnlyExceedMaxDisputeWeight;
  }

  get validationErrorWeightAndColi() {
    return ( !CargoController.isErrorMaxGrossWeight && !CargoController.isErrorMaxVolumeWeight && !CargoController.isErrorMaxPieces);
  }

  get errorInputStepThree() {
    return CargoController.errorInputStepThree;
  }

  get isErrorDiffPiecesActualThanDimensionPieces() {
    if (this.featureHideDimensionKoli) {
      return false;
    }
    return CargoController.isErrorDiffPiecesActualThanDimensionPieces;
  }

  get partnerName() {
    return capitalize(this.dataProfile.account_type_detail.company_name);
  }

  get dataProfile() {
    return AccountController.accountData;
  }

  get typeUser() {
    return this.dataProfile.normalizedTypeUser;
  }

  get detailDistrict() {
    return LocationController.districtDetail;
  }

  get isPlaneAuto() {
    return ( this.formBooking.cargoType === "plane" && this.formBooking.cargoTypeStatus === "auto");
  }

  get isPlaneManual() {
    return ( this.formBooking.cargoType === "plane" && this.formBooking.cargoTypeStatus === "manual");
  }

  get totalStt() {
    return this.sttResult.reduce((prev, curr: any) => {
      if (curr?.sttDetails) return prev + curr.sttDetails.length
      return prev + 1
    }, 0)
  }

  get totalPieces() {
    return CargoController.totalSttPieces;
  }

  openConfirmationSave = false;

  handleConfirmationSave(value: boolean) {
    this.openConfirmationSave = value;
  }

  onCloseSuccess() {
    this.setOpenSuccess(false);
    const today = { hours: formatDate(new Date())?.split(",")[1]?.split(":")[0]?.trim(), minutes: formatDate(new Date())?.split(",")[1]?.split(":")[1]?.replace(/\bwib\b|\bwita\b|\bwit\b|\bgmt\b/gi, '')?.trim()}
    this.isAllowed = ((+this.cutOffTime.hours < +today.hours) || (+this.cutOffTime.hours === +today.hours && +this.cutOffTime.minutes < +today.minutes));
    if (this.isEditCutoff && this.isAllowed) {
      CargoController.setIsFromCutOff(true);
      this.$router.push('/cargo');
    } else {
      if (this.progress === 1 || this.isRebookingBookNow) {
        CargoController.setIsFromCutOff(true);
        this.$router.push('booking-in-progress')
      }

      this.onLeavePage();
    }

    // CargoController.setProgressValue(-1);
  }

  onLeavePage() {
    // if cut off time exceeded with next config, redirect to booking in progress
    // if not / existing, redirect to cargo
    if (this.cutOffTimeExceeded?.cutOffTimeExceededWithNextConfig) {
      this.$emit("leave-page", "booking-in-progress");
    } else {
      this.$emit("leave-page", "/cargo");
    }
  }

  convertProduct(name: string) {
    return name;
  }

  get bagResult(): {
    baggingNumber: string;
    bagGrossWeight: number,
    stt: CargoDetailStt[]
  }[] {
    return CargoController.bagResult;
  }

  get sttResult(): CargoDetailStt[] {
    return CargoController.sttResultFinal;
  }

  togglePanel(event: any) {
   if (
      this.formBooking.cargoType === "plane" &&
        this.formBooking.actualTotalSTTPieces > 15 &&
        this.formBooking.cargoTypeStatus === "manual"
    ) this.showPopupMaxKoliPlane();
    else if (this.isFormBookingValid) {
      const refs: any = this.$refs;
      refs?.op?.toggle(event);
    }
  }

  showPopupMaxKoliPlane() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("Maksimal Koli Pesawat adalah 15 koli"),
        image: "max-koli-plane",
        message: this.$t(
          "Mohon ubah jumlah koli di dalam kargo atau pilih transportasi kargo lainnya"
        ),
        textCancel: this.$t("Transportasi Lain"),
        textSuccess: this.$t("Ubah Koli"),
        onClose: () => MainAppController.closeMessageModal(),
        onSubmit: () => MainAppController.closeMessageModal()
      })
    );
  }

  get tokenNgen() {
    return getTokenNGen();
  }

  get showSelectAirport() {
    return CargoController.showSelectAirport;
  }

  set showSelectAirport(bool: boolean) {
    CargoController.setShowSelectAirport(bool);
  }

  get isRebooking() {
    return this.flowBooking === "rebooking" && this.$route.query.rcId;
  }

  get isRTC() {
    return ReadyToCargoController.flowBooking.includes('rtc') || this.isRebooking;
  }

  async initPlaneAuto(data: any): Promise<void> {
    await this.fetchAirportOrigin(this.originCity.code || "");
    await this.fetchAirportDestination(data.destinationCityCode || "");
  }

  fetchAirportOrigin(search: string) {
    search = search || "";
    search = search.split(" - ").length > 1 ? search.split(" - ")[1] : search;
    return CargoController.getAirportListOrigin({
      airportCode: "",
      cityCode: search,
      page: 1,
      limit: 10,
      isLocation: false
    });
  }
  fetchAirportDestination(search: string) {
    search = search || "";
    search = search.split(" - ").length > 1 ? search.split(" - ")[1] : search;
    return CargoController.getAirportListDestination({
      airportCode: "",
      cityCode: search,
      page: 1,
      limit: 10
    });
  }

  async showModalPlaneAuto(isAuthNgen: any, isFromCuttOff = false) {
    CargoController.form.airports = [];
    CargoController.setIsBookingAuto(true);
    const isAuth = isAuthNgen ? true : await NGenController.onLoginNGen();
    const refs: any = this.$refs;
    refs?.op?.hide();
    if (isAuth) {
      await this.initPlaneAuto({
        destinationCityCode: this.formBooking.destinationCityCode
      });
      CargoController.setAirportsPopupModel(
        JSON.parse(JSON.stringify(CargoController.form.airports))
      );
      CargoController.setShowSelectAirportToLocalStorage(true);
      CargoController.setAirportsPopupModel(
        JSON.parse(JSON.stringify(CargoController.form.airports))
      );
      this.formBooking.transit = "0";
      this.showSelectAirport = true;
    }
  }

  get showModalConfirm() {
    return CargoController.showModalConfirm;
  }

  showModalComplete() {
    CargoController.setIsBookingAuto(false);
    const refs: any = this.$refs;
    refs?.op?.hide();
    CargoController.setShowModalCompleteCargo(true);
  }

  get iconTogglePanel() {
    const refs: any = this.$refs;
    return refs?.op?.visible ? "chevron-up-white" : "chevron-down-white";
  }

  isAllowed: any = false;

  onBookingRtc() {
    CargoController.setIsBookingAuto(false);
    const refs: any = this.$refs;
    refs.op.hide();
    if (this.validationMaxPieces) {
      this.showModalMaxPieces();
      return;
    }

    this.handleConfirmationSave(true);
  }

  showModalMaxPieces() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("Maksimal Koli Pesawat adalah 15 koli"),
        image: "max-koli-plane",
        message: this.$t(
          "Mohon ubah jumlah koli di dalam kargo atau pilih transportasi kargo lainnya"
        ),
        textSuccess: "OK",
        onSubmit: () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  get validationMaxPieces() {
    const validation =
      CargoController.cargoType.type === "plane" &&
      CargoController.form.actualTotalSTTPieces > 15 &&
        this.formBooking.cargoTypeStatus === "manual"
      // !FlagsPermissionCargo.feature_flight_v2_enable.isEnabled();

    return validation;
  }

  async checkEligible(isBookNow = true) {
    checkEligible(() => {
      if (isBookNow) this.createAndPrint(false);
        else this.showModalPlaneAuto(true);
    }, () => {
      if (CargoController.isEditReadyToCargo) {
        if (!CargoController.sttResultFinal.length) {
          // redirect to list rtc
          CargoController.setProgressValue(-1);
          CargoController.setFillForm(false);
          CargoController.resetCargoData();
          ReadyToCargoController.setFlowBooking("");
        }
        return
      } 
      }
    )
  }

  createAndPrint(withPrint: boolean, label = false) {
    const refs: any = this.$refs;
    refs.op.hide();
    CargoController.setWithPrintCutOff(false);
    CargoController.setWithPrintLabelCutOff(false);
    this.withPrint = withPrint;
    this.withPrintLabel = label;
    if (this.validationMaxPieces) {
      this.showModalMaxPieces();
      return;
    }
    if (this.isEditCutoff && this.isAllowedValidation) {
      CargoController.setWithPrintCutOff(withPrint);
      CargoController.setWithPrintLabelCutOff(label);
      this.$emit('onCheckCutOffEditRTC');
      return;
    }
    CargoController.setShowModalCompleteCargo(false);
    CargoController.setIsBookingAuto(false);
    this.handleConfirmationSave(true);
  }

  get isAllowedValidation() {
    const now = new Date();
    const today = {
      hours: now.getHours(),
      minutes: now.getMinutes(),
    };

    const cutOffHours = +this.cutOffTime.hours;
    const cutOffMinutes = +this.cutOffTime.minutes;

    return (
      cutOffHours < today.hours ||
      (cutOffHours === today.hours && cutOffMinutes < today.minutes)
    ) && CargoController.progressValue !== -1;
  }

  get isEditCutoff(){
    return this.isAllowEditCutoff;
  } 

  get isAllowEditCutoff(){
    const isEditRtcCutoff = ReadyToCargoController.flowBooking === 'rtc-edit' && this.isPlaneAuto && this.cutOffTime.hours !== undefined && this.cutOffTime.minutes !== undefined
    return isEditRtcCutoff;
  } 

  get cutOffTime() {
    return {hours: ReadyToCargoController.readyToCargoDetail.cutOffTime?.split(":")[0], minutes: ReadyToCargoController.readyToCargoDetail.cutOffTime?.split(":")[1] };
  }

  withPrint = false;
  withPrintLabel = false;
  countSuccess = 0;
  countFailed = 0;

  get rcId(): number {
    return Number(this.$route.query.rcId);
  }

  // origin city
  get originCity(): OptionsClass {
    const detailDistrict = LocationController.districtDetail;
    return new OptionsClass({
      name: `${detailDistrict.cityCode} - ${firstCapitalize(
        detailDistrict.cityName.toLowerCase()
      )}`,
      code: detailDistrict.cityCode
    });
  }
  get destinationCity(): OptionsClass {
    return CargoController.destinationCity;
  }

  get isRtc() {
    return (
      ReadyToCargoController.flowBooking === "rtc-process" ||
      ReadyToCargoController.flowBooking === "rtc-edit"
    );
  }

  get withPrintCutOff() {
    return CargoController.withPrintCutOff;
  }

  get withPrintCutOffLabel() {
    return CargoController.withPrintCutOffLabel;
  }

  showModalErrorBookingCargo() {
    MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: "Pembuatan Gagal !",
            message:
              "Pengiriman kargo gagal dibuat",
            textCancel: "OK",
            textSuccess: "Download",
            image: "image-modal-failed",
            onClose: () => {
              MainAppController.closeMessageModal();
              if (this.progress === 1) {
                CargoController.setIsFromCutOff(true);
                this.$router.push('booking-canceled')
              }
              this.$emit("leave-page", "/cargo/booking-canceled");
            },
            onSubmit: () => this.onDownloadCsvFailedStt(CargoController.sttFailed)
          })
        );
  }

 showPopupTimeout(resp: any) {
    if (resp.bookingRequestId) { //success RTC Ngen
      this.showModalMessageBookingOnProcess(
        `Booking Kargo ${resp.bookingRequestId} <br/> Sedang Diproses`
      );
      this.bookingCargoGTM(this.eventName, "success");
    } else { //timeout RTC Ngen
      //timeout RTC Ngen
      if (resp.cargoNo) {
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: "Booking Kargo Gagal Diproses nGen",
            message:
              "Lihat alasannya di halaman gagal booking. Cek & booking ulang",
            textSuccess: "OK",
            image: "image-modal-failed",
            onSubmit: () => {
              MainAppController.closeMessageModal();
              if (this.progress === 1) {
                CargoController.setIsFromCutOff(true);
                this.$router.push('booking-canceled')
              }
              this.$emit("leave-page", "/cargo/booking-canceled");
            }
          })
        );
      } else {
        if (resp.totalSttFailed && !resp.totalSttSuccess) {
         this.countFailed = resp.totalSttFailed;
        CargoController.setSttFailed(resp.listSttFailed);
        this.showModalErrorBookingCargo();

        // GTM
        this.bookingCargoGTM(this.eventName, "failed");
        const gtmBookingCargoFailed = gtmCargoData({
          type: this.formBooking.cargoType,
          isAuto: this.isPlaneAuto
        }).bookingCargoFailed;

        dataLayer(
          gtmBookingCargoFailed.eventName,
          gtmBookingCargoFailed.property,
          ["timestamp"]
        );
      } else if (resp.totalSttFailed && resp.totalSttSuccess) {
        this.countFailed = resp.totalSttFailed;
        this.countSuccess = resp.totalSttSuccess;
        this._openModalPartial();
        CargoController.setSttFailed(resp.listSttFailed);
        this.onPrint();
      }
    }
    }
    MainAppController.closeLoading();
  }

  get payloadOriginAirportCode() {
    if (this.isRebookingBookNow)
      return this.cargoInputFormData.airportOriginCode;
    return this.flowBooking === "rtc-edit" ||
      this.formBooking.cargoTypeStatus === "manual"
      ? this.formBooking.airportOrigin?.airportCode ||
          LocationController.districtDetail.cityCode
      : this.cargoPlaneAutoData?.flightData[0]?.flight_origin;
  }
  get payloadDestinationAirportCode() {
    if (this.isRebookingBookNow) {
      return this.cargoInputFormData.airportDestinationCode;
    }
    const lastIndex = this.cargoPlaneAutoData?.flightData?.length - 1;
    return this.flowBooking === "rtc-edit" ||
      this.formBooking.cargoTypeStatus === "manual"
      ? this.formBooking.airportDestination.airportCode
      : this.cargoPlaneAutoData?.flightData[lastIndex]?.flight_destination;
  }


  get payloadIsFromRtc() {
    const cargoInputForm = JSON.parse(
      getDataSessionStorage("cargo-input-data") || "{}"
    );
    return this.isRtc || this.dataDetailRetryCargo.isFromRtc || cargoInputForm.isFromRebookingRTC
  }

  get bagOrSttNo() {
    const cargoBagRaw = getDataSessionStorage("cargo-bag") || "[]";
    const cargoRaw = getDataSessionStorage("cargo") || "[]";

    const cargoBag = JSON.parse(cargoBagRaw);
    const cargo = JSON.parse(cargoRaw);

    const payloadBagNoOnly = cargoBag
      .map((item: any) => new AddBagOrStt(item.baggingNumber))
      .filter((item: any) => item != null);

    const payloadSttNoOnly = cargo
      .filter((item: any) => {
        const isAlreadyBagged = cargoBag.some((bag: any) =>
          JSON.stringify(bag).includes(item.sttNumber)
        );

        return !isAlreadyBagged;
      })
      .flatMap((item: any) => {
        if (item.sttDetails) {
          return item.sttDetails.map(
            (detail: any) => new AddBagOrStt(undefined, detail.sttNumber)
          );
        }
        return new AddBagOrStt(undefined, item.sttNumber);
      });

    return [...payloadBagNoOnly, ...payloadSttNoOnly];
  }

  get totalSttChargeableWeight() {
    return CargoController.totalSttChargeableWeight;
  }

  async calculateVolume(calculateType = "") {
    await CargoController.calculateVolume(new CalculateVolumeApiRequest({
      calculateType,
      calculateRequest: new CalculateVolumeRequest({
        cargoType: this.formBooking.cargoType,
        chargeableWeightReweigh: this.chargeableWeight,
        currentGrossWeight: this.parseNumber(this.formBooking.actualTotalCargoGrossWeight),
        height: this.formBooking.actualDimension.height,
        length: this.formBooking.actualDimension.length,
        width: this.formBooking.actualDimension.width,
        totalPieces: this.formBooking.actualTotalSTTPieces,
        volumeWeightReweigh: this.parseNumber(this.totalNewCalculationVolumeWeights as any),
        divider: getConfigDimensionDivider(this.formBooking.cargoType),
        isMinimumVolumePerPiece: getConfigIsminimumVolumePerPiece(this.formBooking.cargoType, this.formBooking.cargoTypeStatus),
        minVolumePerPiece: FlagsPermissionCargo.validation_3lc_cargo_minimum_weight.getValue()
      })
    }))
  }

  async onSave(): Promise<void> {
    this.handleConfirmationSave(false);
    if (
      this.formBooking.cargoType === "plane" &&
      this.calculateFlightDate.length &&
      this.flowBooking === "rtc-process"
    ) {
      this.formBooking.airportOrigin.value = this.calculateFlightDate[0]?.flight_origin;
      this.formBooking.airportDestination.value = this.calculateFlightDate[
        this.calculateFlightDate.length - 1
      ]?.flight_destination;
    }
    CargoController.setTimestampBooking(new Date().getTime());
    await this.calculateVolume(CargoController.calculateVolumeData.calculateType);
    await this.$nextTick();
    const resp = await CargoController.onCreateCargo({
      originAirportCode: this.payloadOriginAirportCode,
      destinationAirportCode: this.payloadDestinationAirportCode,
      estTotalCargoGrossweigth: this.validationPayloadTimbangUlang
        .estTotalCargoGrossweigth,
      estTotalCargoVolumeweight: this.validationPayloadTimbangUlang
        .estTotalCargoVolumeweight,
      partnerName: this.partnerName,
      partnerType: this.typeUser,
      cargoNo: this.cargoNumberPayload,
      cargoVehicleNo: this.vehicleNumberPayload,
      actualTotalCargoGrossweigth: this.validationPayloadTimbangUlang
        .actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight: this.validationPayloadTimbangUlang
        .actualTotalCargoVolumeweight,
      route: this.routePayload, // from nGen
      totalStt: this.totalStt,
      totalPiece: this.totalPieces,
      cargoCommodity: this.formBooking.cargoCommodityCode,
      productCargo: this.formBooking.cargoProduct,
      cargoType: this.formBooking.cargoType,
      originCityCode: this.detailDistrict.cityCode,
      destinationCityCode: this.formBooking.destinationCityCode,
      departureDate: this.departureDatePayload,
      arrivalDate: this.arrivalDatePayload,
      bagOrStt: this.bagOrSttNo,
      isCargoPlaneManual: this.formBooking.cargoType === "plane" && this.formBooking.cargoTypeStatus === "manual",
      cargoOverCutoffLimit: this.payloadOverCutOffLimit,
      cargoRtcTurnAuto: true,
      cargoActualPiece: this.cargoActualPiece,
      flightPlanForECargo: this.flightPlaneForECargoPayload,
      dimensions: this.validationPayloadTimbangUlang.dimensions,
      estDimension: this.formBooking.estDimension,
      actualDimension: this.validationPayloadTimbangUlang.actualDimension,
      isFromRtc: this.payloadIsFromRtc,
      rtcId: this.isRtc ? ReadyToCargoController.readyToCargoDetail.id : 0,
      nog: this.formBooking.nog,
      rebookingId: this.rcId,
      ngenCreatedAt: dateTimePayload(this.formBooking.ngenCreatedAt),
      shc: this.formBooking.shcCode,
      photoProof: this.formBooking.photoProof.filter((photo: any) => typeof photo === "string").filter(Boolean),
      cargoTotalChargeableWeight: this.totalSttChargeableWeight,
      cargoTotalCustomChargeableWeight: this.actualChargeableWeight,
      cargoTotalEstimateChargeableWeight: this.chargeableWeight,
      isNewCalculation:
        this.detail.isUseNewCalculation ||
        flags.feature_hide_dimension_koli.isEnabled(),
      calculateType: CargoController.calculateVolumeData.calculateType,
      isCargoDimensionOn: CargoController.isCargoDimensionOn
    })
      .catch((err: any) => {
        this.onErrorCreateCargo(err, this.onSave);
      })
      .finally(() => {
        MainAppController.closeMessageModal();
      });
    this.onCreateResponse(resp);
    this.handleConfirmationSave(false);
  }
  get featureHideDimensionKoli(): boolean {
    return flags.feature_hide_dimension_koli.isEnabled();
  }
  get isUsePayloadTimbangUlang() {
    return (
      this.formBooking.cargoType !== "plane" || this.featureHideDimensionKoli
    );
  }
  get dimensionTimbangUlang() {
    const volumeWeight = Math.round(
      this.totalNewCalculationVolumeWeightsMaxFlag
    );

    const divider = getConfigDimensionDivider(this.formBooking.cargoType);


    const pieces = this.isRtc
      ? this.detail.totalPieces
      : CargoController.totalSttPieces;

    const length = 90;
    const width = 60;
    const height = Math.max(1, Math.floor(
      (volumeWeight * divider) / (length * width * pieces)
    ));

    return {
      length,
      width,
      height,
      pieces
    };
  }
  get validationPayloadTimbangUlang() {
    const actualTotalCargoGrossweigth = this.getActualCargoWeight(
      CargoController.dimensionPerKoliData.actualGrossWeight,
      this.formBooking.actualTotalCargoGrossWeight
    );


    if (this.isUsePayloadTimbangUlang) {
      return {
        actualDimension: this.formBooking.actualDimension,
        dimensions: this.mappingDetailPieces,
        actualTotalCargoGrossweigth,
        actualTotalCargoVolumeweight: this.parseNumber(this.formBooking.actualTotalCargoVolumeWeight as any),
        estTotalCargoGrossweigth: CargoController.totalNewCalculationGrossWeights,
        estTotalCargoVolumeweight: this.totalNewCalculationVolumeWeights
      };
    }

    return {
      actualDimension: this.formBooking.actualDimension,
      dimensions: this.mappingDetailPieces,
      actualTotalCargoGrossweigth,
      actualTotalCargoVolumeweight: this.actualCargoVolumeWeightPayload,
      estTotalCargoGrossweigth: commaDecimalToDecimal(
        this.formBooking.estTotalCargoGrossWeight
      ),
      estTotalCargoVolumeweight: commaDecimalToDecimal(
        this.formBooking.estTotalCargoVolumeWeight
      )
    };
  }

  onErrorCreateCargo(err: any, onRetry: () => void) {
    MainAppController.showErrorMessage(
      parsingErrorResponse(
        err,
        this.isAccountForeign
          ? "Failed to Cargo Booking !"
          : "Pembuatan Gagal !",
        () => {
          MainAppController.closeErrorMessage();
          onRetry();
        },
        () => {
          handleFailedBookingNavigation({
            router: this.$router,
            rcId: this.$route.query.rcId,
            cargoType: this.formBooking.cargoType,
            cargoTypeStatus: this.formBooking.cargoTypeStatus,
            flowBooking: this.flowBooking
          });
        }
      )
    );
  }

get isRebookingBookNow() {
    const cargoData = new CargoData({isUseSabre: this.cargoInputFormData.isUseSabre, cargoType: this.formBooking.cargoType})
    return (
      this.isRebooking &&
      cargoData.isUseSabreForRebooking &&
        !this.cargoInputFormData.isOverCutoffTime &&
        this.cargoInputFormData.reasonFailed.toLowerCase() !== "penerbangan tidak tersedia" &&
        !CargoController.isChangeCargoType
    )
  }

  get cargoNumberPayload() {
    return this.isRebookingBookNow ? this.formBooking.cargoNumber : this.cargoPlaneAutoData.cargoNumber;
  }

  get vehicleNumberPayload() {
    return this.isRebookingBookNow ? this.formBooking.vehicleNumber : this.cargoPlaneAutoData.vehicleNumber;
  }

  get departureDatePayload() {
    return this.isRebookingBookNow ? dateTimePayload(this.formBooking.estDepartureDate) : dateTimePayload(this.cargoPlaneAutoData.estDepartureDate);
  }

  get arrivalDatePayload() {
    return this.isRebookingBookNow ? dateTimePayload(this.formBooking.estArrivalDate) : dateTimePayload(this.cargoPlaneAutoData.estArrivalDate);
  }

  get flightPlaneForECargoPayload(): any {
    return this.isRebookingBookNow ? this.cargoInputFormData.flightPlaneForECargo : this.cargoPlaneAutoData.flightData
  }

  get routePayload() {
    const origin = this.originAirportCodePayload;
    const destination = this.destinationAirportCodePayload;

    return this.isRebookingBookNow ? `${origin}-${destination}` : this.cargoPlaneAutoData.route;
  }

  get originAirportCodePayload() {
    if (this.isRebookingBookNow) return this.cargoInputFormData.airportOriginCode;
    return this.formBooking.airportOrigin?.airportCode || LocationController.districtDetail.cityCode
  }

  get destinationAirportCodePayload() {
    return this.formBooking.airportDestination.airportCode || this.cargoInputFormData.airportDestinationCode || this.cargoInputFormData.airportDestinationCityCode;
  }

  get cargoPlaneAutoData(): CargoPlaneAutoData {
    const today = { hours: formatDate(new Date())?.split(",")[1]?.split(":")[0]?.trim(), minutes: formatDate(new Date())?.split(",")[1]?.split(":")[1]?.replace(/\bwib\b|\bwita\b|\bwit\b|\bgmt\b/gi, '')?.trim()}
    this.isAllowed = ((+this.cutOffTime.hours < +today.hours) || (+this.cutOffTime.hours === +today.hours && +this.cutOffTime.minutes < +today.minutes)) && CargoController.progressValue !== - 1;
    if (
      this.formBooking.cargoType === "plane" &&
      this.calculateFlightDate.length &&
      this.flowBooking === "rtc-process" && !(!this.detail.cutOffTime && this.detail.cargoType === 'plane')
    ) {
      return new CargoPlaneAutoData({
        estDepartureDate: this.calculateFlightDate[0].flight_dep_datetime,
        estArrivalDate: this.calculateFlightDate[
          this.calculateFlightDate.length - 1
        ].flight_arr_datetime,
        flightData: this.calculateFlightDate
      });
    }
    if (this.isPlaneAuto) {
      const origin =
        this.formBooking.flightsData[0]?.Route.split("-")[0] ||
        this.originCity.code;
      const destination =
        this.formBooking.flightsData[
          this.formBooking.flightsData.length - 1
        ].Route.split("-")[1] || this.destinationCity.code;
      const route = `${origin}-${destination}`;
      return new CargoPlaneAutoData({
        route: !route.includes("undefined") ? route : "",
        estDepartureDate:
          this.formBooking.flightsData[0].DepartureDateTime ||
          this.formBooking.estDepartureDate,
        estArrivalDate: this.formBooking.flightsData[
          this.formBooking.flightsData.length - 1
        ].ArrivalDateTime,
        flightData: this.isRtc && !(!this.detail.cutOffTime && this.detail.cargoType === 'plane') && !this.isChangeCargoType && !(this.isEditCutoff && this.isAllowed) ? this.calculateFlightDate : this.flightData 
      });
    }
    return new CargoPlaneAutoData({
      estDepartureDate: this.formBooking.estDepartureDate,
      estArrivalDate: this.formBooking.estArrivalDate,
      cargoNumber: this.formBooking.cargoNumber,
      vehicleNumber: this.formBooking.vehicleNumber
    });
  }
  get flightData() {
    let data = this.formBooking.flightsData.map(
      item =>
        new FlightPlanForECargo( // from ngen
          this.dailyFlightNoFlightData(item),
          true, // isRootStation
          item.FlightNo,
          item.DepartureDateTime ? this.$moment(item.DepartureDateTime.split(" ")[0]).format( "YYYY-MM-DD") : "",
          item.Route.split("-")[0] || item.airportCodeOrigin,
          item.Route.split("-")[1] || item.airportCodeDestination,
          this.departureDateTime(item),
          this.arivalDateTime(item),
          this.departureDateTime(item),
          item.thresholdTime
        )
    );
    if(this.isRtc || (this.flowBooking === "rebooking" && this.$route.query.rcId)) {
      data = data.map((item: any) => {
        return {
          ...item,
          flight_threshold_time: CargoController.isShowPlaneSchedule
            ? +this.formBooking.thresholdTime
            : item.flight_threshold_time
        };
      });
    }
    return data;
  }

  get thresholdPayload() {
    return CargoController.isShowPlaneSchedule ? +this.formBooking.thresholdTime : this.detail.nextFlightConfigTreshold;
  }

  get isFromRebookingRTC() {
    return CargoController.isRebookingFromRTC;
  }

  get cargoInputForm() {
    return CargoController.cargoInputForm;
  }

  get cargoInputFormData() {
    const cargoInputFormLocalstorage = JSON.parse( getDataSessionStorage("cargo-input-data") || "{}")
    let cargoInputForm: any = CargoController.cargoDetailData;
    if (cargoInputFormLocalstorage.id) {
      cargoInputForm = cargoInputFormLocalstorage;
    }

    return cargoInputForm;
  }

  departureDateTime(item: any) {
    return item.DepartureDateTime ? this.$moment(item.DepartureDateTime).format("YYYY-MM-DD HH:mm:ss") : "";
  }

  arivalDateTime(item: any) {
    return item.ArrivalDateTime ? this.$moment(item.ArrivalDateTime).format("YYYY-MM-DD HH:mm:ss") : "";
  }

  dailyFlightNoFlightData(item: any) {
    return !this.formBooking.airportOrigin.name && !this.formBooking.airportDestination.name ? "" : `${item.DailyFlightSNo}`;
  }

  // failed for create
  isPartial = false;
  get timestampBooking() {
    return CargoController.timestampBooking;
  }

  get isChangeCargoType() {
    return CargoController.isChangeCargoType
  }
  get isFailed(): boolean {
    return CargoController.isFailed;
  }

  get flowBooking() {
    return ReadyToCargoController.flowBooking;
  }

  get detail() {
    return ReadyToCargoController.readyToCargoDetail;
  }

  get calculateFlightDate() {
    return ReadyToCargoController.calculateFlightDate.map((key)=> {
      return {
        ...key,
        flight_threshold_time:
          CargoController.isShowPlaneSchedule ?
          +this.formBooking.thresholdTime :
          key.flight_threshold_time
      }
    });
  }

  setFailed(value: boolean) {
    CargoController.setFailed(value);
  }

  showModalMessageBookingOnProcess(title: string) {
    const popupIllustration =
      this.formBooking.cargoType === "plane"
        ? "on-process-booking-cargo"
        : "illustration_waiting_process";
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title,
        message: "Anda akan mendapat notifikasi jika booking kargo berhasil",
        textSuccess: "OK",
        image: popupIllustration,
        onSubmit: () => {
          MainAppController.closeMessageModal();
          this.onCloseSuccess();
        },
      })
    );
  }



  onCreateResponse(resp: any) {
    if (resp) {
      CargoController.resetScanFailedList();

      if (this.formBooking.cargoType === "plane" && this.formBooking.cargoTypeStatus === "auto" && (this.isRtc || (this.isRebooking && this.isFromRebookingRTC)) && flags.feature_is_booking_cargo_v4.isEnabled()) {
        this.showPopupTimeout(resp);
        return;
      }
        else if (resp.cargoNo.startsWith("FLIGHT") || resp.isCargoUsingAwb) { //error Ngen Non RTC
        this.showModalMessageBookingOnProcess("Booking Kargo Sedang Diproses");
        this.bookingCargoGTM(this.eventName, "success");
      } else if (resp.totalSttFailed && !resp.totalSttSuccess) {
        CargoController.setSttFailed(resp.listSttFailed);
        this.countFailed = resp.totalSttFailed;
        this.showModalErrorBookingCargo();

        // GTM
        this.bookingCargoGTM(this.eventName, "failed");
        const gtmBookingCargoFailed = gtmCargoData({
          type: this.formBooking.cargoType,
          isAuto: this.isPlaneAuto
        }).bookingCargoFailed;

        dataLayer(
          gtmBookingCargoFailed.eventName,
          gtmBookingCargoFailed.property,
          ["timestamp"]
        );
      } else if (resp.totalSttFailed && resp.totalSttSuccess) {
        this.countFailed = resp.totalSttFailed;
        this.countSuccess = resp.totalSttSuccess;
        this._openModalPartial()
        CargoController.setSttFailed(resp.listSttFailed);
        this.onPrint();
      } else {
        // GTM
        this.bookingCargoGTM(this.eventName, "success");
        const gtmBookingCargoSuccess = gtmCargoData({
          type: this.formBooking.cargoType,
          isAuto: this.isPlaneAuto,
          gtmData: {
            cargoNumber: CargoController.cargoIdToGeneratePdf,
            flightNumber: this.formBooking.vehicleNumber
          }
        }).bookingCargoSuccess;

        dataLayer(
          gtmBookingCargoSuccess.eventName,
          gtmBookingCargoSuccess.property,
          ["timestamp"]
        );

        this._openModalSuccess();
        this.onPrint();
      }
    }
  }

  _openModalSuccess() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        customWidth: "360px",
        title: this.$t("Pembuatan Berhasil !"),
        message: this.$t("cargo.successCreateMessage", {cargoNo: CargoController.cargoIdToGeneratePdf}),
        image: "image-modal-success",
        textSuccess: "OK",
        onSubmit: () => {
          this.onCloseSuccess();
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  _openModalPartial() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        customWidth: "480px",
        title: this.$t("Pembuatan Berhasil !"),
        message: this.$t("cargo.partialCreateMessage", {
          cargoNo: CargoController.cargoIdToGeneratePdf
        }),
        image: "image-modal-warning",
        textSuccess: "Download",
        textCancel: "OK",
        onSubmit: () => {
          this.onDownloadCsvFailedStt(CargoController.sttFailed);
        },
        onClose: () => {
          this.onCloseSuccess();
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  bookingCargoGTM(eventName: any, result: string) {
    GTMBookingCargoEvent(eventName, {
      cargo_type: this.formBooking.cargoType,
      "3LC_origin": this.detailDistrict.cityCode,
      "3LC_destination": this.formBooking.destinationCityCode,
      booking_result: result,
      cargo_number: CargoController.cargoIdToGeneratePdf,
      timestamp_booking: this.timestampBooking,
      timestamp_get_result: new Date().getTime()
    });
  }

  get eventName() {
    return ReadyToCargoController.eventName;
  }

  onPrint() {
    const refs: any = this.$refs;
    if (this.withPrint || this.withPrintCutOff) {
      refs.print.printManifestV2(CargoController.cargoIdToGeneratePdf);

      // GTM
      const gtmBookingCargoSuccessPDF = gtmCargoData({
        type: this.formBooking.cargoType,
        isAuto: this.isPlaneAuto,
        gtmData: {
          cargoNumber: CargoController.cargoIdToGeneratePdf,
          flightNumber: this.formBooking.vehicleNumber
        }
      }).bookingCargoSuccessPDF;

      dataLayer(
        gtmBookingCargoSuccessPDF.eventName,
        gtmBookingCargoSuccessPDF.property,
        ["timestamp"]
      );
    }
    if (this.withPrintLabel || this.withPrintCutOffLabel) {
      refs.print.printData(CargoController.cargoIdToGeneratePdf, "label");

      // GTM
      const gtmBookingCargoSuccessLabel = gtmCargoData({
        type: this.formBooking.cargoType,
        isAuto: this.isPlaneAuto,
        gtmData: {
          cargoNumber: CargoController.cargoIdToGeneratePdf,
          flightNumber: this.formBooking.vehicleNumber
        }
      }).bookingCargoSuccessLabel;

      dataLayer(
        gtmBookingCargoSuccessLabel.eventName,
        gtmBookingCargoSuccessLabel.property,
        ["timestamp"]
      );
    }
  }

  get openSuccess(): boolean {
    return CargoController.isOpenSuccess;
  }

  setOpenSuccess(value: boolean) {
    CargoController.setOpenSuccess(value);
  }

  get isShowPopup(): boolean {
    return (
      this.openConfirmationSave || this.openSuccess || this.isPartial || this.isFailed
    );
  }

  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get popupModalData() {
    const popupIllustration =
      this.formBooking.cargoType === "plane"
        ? "badge-confirmation-general"
        : "are-you-sure";
    // confirmation save
    if (this.openConfirmationSave) {
      return {
        success: {
          action: this.onCheckEligible,
          text: this.isAccountForeign ? "Create" : "Process"
        },
        cancel: {
          action: this.handleConfirmationSave,
          text: this.$t("Batal")
        },
        title: this.$t("cargo.confirmCreateCargoTitle"),
        message: this.$t("Pastikan kembali, seluruh detail STT sudah benar dan sesuai."),
        image: popupIllustration
      };
    }
    // if success
    if (this.openSuccess) {
      return {
        success: {
          action: this.onCloseSuccess,
          text: "OK"
        },
        title: this.$t("Pembuatan Berhasil !"),
        message: this.$t("cargo.successCreateMessage", {cargoNo: CargoController.cargoIdToGeneratePdf}),
        image: "image-modal-success"
      };
    }

    // if success partially
    if (this.isPartial) {
      return {
        success: {
          action: () => this.onDownloadCsvFailedStt(this.sttFailedUpdate),
          text: "Download"
        },
        cancel: {
          action: this.onCloseSuccess,
          text: "OK"
        },

        title: this.$t("Pembuatan Berhasil !"),
        message: this.$t("cargo.partialCreateMessage", {cargoNo: CargoController.cargoIdToGeneratePdf}),
        image: "image-modal-warning"
      };
    }

    // default is failed
    return {
      success: {
        action: () => this.onDownloadCsvFailedStt(this.sttFailedUpdate),
        text: "Download"
      },
      cancel: {
        action: () => this.setFailed(false),
        text: "OK"
      },
      title: this.$t("Pembuatan Gagal!"),
      message: this.$t("Pengiriman kargo gagal dibuat."),
      image: "image-modal-failed"
    };
  }

  onCheckEligible() {
    this.handleConfirmationSave(false);

    const close = () => {
      const isRtc = new RegExp(/rtc/, "i").test(
        ReadyToCargoController.flowBooking
      );
      if (isRtc && !CargoController.sttResultFinal.length) {
        // redirect to list rtc
        CargoController.setProgressValue(-1);
        CargoController.setFillForm(false);
        CargoController.resetCargoData();
        ReadyToCargoController.setFlowBooking("");
        return;
      }

      // keep if at step 3
      if (
        !CargoController.isShowPlaneSchedule &&
        CargoController.sttResultFinal.length
      ) {
        return;
      }

      // back to form scan
      if (this.$route.query.rcId) {
        this.$emit("leave-page", "booking-canceled");
        return
      };

      const flowBooking = JSON.parse(
        getDataSessionStorage("flow-booking") || "{}"
      );
      if (flowBooking === "manual") {
        CargoController.setCargoType({});
      } else {
        ReadyToCargoController.setFlowBooking("rtc-edit");
        CargoController.setCargoOptions({ data: [] });
        CargoController.setIsEditReadyToCargo(true);
        CargoController.setIsFirstLandingEditRTC(true);
        CargoController.setShowCargoOptions(false);
      }

      CargoController.setProgressValue(0);
      CargoController.setDefaultFlightData();
      CargoController.setEditFlightData([]);
      CargoController.mappingCommodityOptions();
    };

    checkEligible(this.onSave, close);
  }

  get sttFailedUpdate() {
    return CargoController.sttFailed;
  }

  get dataDetailRetryCargo() {
    return CargoController.cargoDetailData;
  }

  setPartial(value: boolean) {
    this.isPartial = value;
  }

  onDownloadCsvFailedStt(data: any) {
    this.downloadCsvFailedStt({
      fileName: "cargo_stt_failed.csv",
      listStt: data
    });
  }

  get formDetailPieces() {
    return CargoController.formDetailPieces;
  }

  get isHaveBeenSubmitDetailPieces() {
    return CargoController.isHaveBeenSubmitDetailPieces;
  }

  get isValidDimension(): boolean {
    return CargoController.dimensionPerKoliData.dimensionPieces.every(
      e => e.sttOrBag
    )
  }
  get mappingDetailPieces() {
    if (this.isRtcDimension) {
      return this.dimensionV2Payload;
    }

    return this.dimensionV1Payload;
  }

  get dimensionV1Payload() {
    if (!this.isRtcDimension) {
      const { height, length, width } = this.formBooking.actualDimension;
      return [
        new DimensionPieces({
          pieces: +this.formBooking.actualTotalSTTPieces,
          length: length,
          width: width,
          height: height,
          grossWeight: undefined,
          sttOrBag: undefined,
          volumeWeight: undefined
        })
      ];
    }

    return this.formDetailPieces.dimensionPieces.map((item: any) => {
      return {
        pieces: !this.isHaveBeenSubmitDetailPieces ? +this.formBooking.actualTotalSTTPieces : +item.pieces,
        length: !this.isHaveBeenSubmitDetailPieces ? this.formBooking.actualDimension.length : item.length,
        width: !this.isHaveBeenSubmitDetailPieces ? this.formBooking.actualDimension.width : item.width,
        height: !this.isHaveBeenSubmitDetailPieces ? this.formBooking.actualDimension.height : item.height,
        "gross_weight": item.sttOrBag ? item.grossWeight : undefined,
        "volume_weight": item.sttOrBag ? item.volumeWeight : undefined,
        "stt_or_bag": item.sttOrBag
      };
    });
  }
  get dimensionV2Payload() {
    return CargoController.dimensionPerKoliData.dimensionPieces.map(
      (item: DimensionPerKoliDetail) => ({
        stt_or_bag: item.sttOrBag,
        pieces: item.totalPieces,
        length: item.sttPieceLength,
        width: item.sttPieceWidth,
        height: item.sttPieceHeight,
        gross_weight: item.sttPieceGrossWeight,
        volume_weight: item.sttPieceVolumeWeight
      })
    );
  }

  get actualCargoVolumeWeightPayload() {
    return this.getActualCargoWeight(
      CargoController.dimensionPerKoliData.actualVolumeWeight,
      this.formBooking.actualTotalCargoVolumeWeight
    );
  }

  private getActualCargoWeight(dimensionWeight: number, totalWeight: string) {
    const isRtcOrRebooking =
      this.isRTC && this.formBooking.cargoType === "plane";
    const dimensionsPlaneAuto =
      !flags.feature_hide_dimension_koli.isEnabled() &&
      this.isValidDimension && !this.isPlaneManual &&
      (this.isPlaneAuto || isRtcOrRebooking);

    return dimensionsPlaneAuto && !this.isPlaneManualAwbValid
      ? Number(dimensionWeight.toFixed(2))
      : commaDecimalToDecimal(totalWeight);
  }

  get cargoActualPiece() {
    return this.isDimensionPlaneAutoV2 && !this.isPlaneManualAwbValid
      ? CargoController.dimensionPerKoliData.totalPieces
      : +this.formBooking.actualTotalSTTPieces;
  }
  get isPlaneManualAwbValid() {
    return (
      CargoController.isAwbValid &&
      this.formBooking.cargoType === "plane" &&
      this.formBooking.cargoTypeStatus === "manual"
    );
  }
  get isDimensionPlaneAutoV2(): boolean {
    const isRtcOrRebooking = this.isRTC && this.isPlaneAuto;
    return Boolean(
      !flags.feature_hide_dimension_koli.isEnabled() &&
        this.isValidDimension &&
        isRtcOrRebooking
    );
  }

  get payloadOverCutOffLimit() {
    const cargoInputForm = JSON.parse(
      getDataSessionStorage("cargo-input-data") || "{}"
    );
    return CargoController.isOverCutOffTime || this.detail.isMoreThanCutOffTime || cargoInputForm.isOverCutOffTime;
  }

  get isRtcDimension() {
    const isRtcAndPlaneAuto = this.isRTC && this.isPlaneAuto;
    return !flags.feature_hide_dimension_koli.isEnabled() &&
      this.isValidDimension &&
      (this.isPlaneAuto || isRtcAndPlaneAuto);
  }

  get totalGrossWeights() {
    return CargoController.totalGrossWeights || "0";
  }

  private parseNumber(value: string) {
    if (typeof value === "number") return value;
    return value ? parseByCountry(value, { country: this.countryCode }) : 0;
  }

  get isValidationAdjustWeightProofPhotoEnable() {
    return FlagsPermissionCargo.validation_adjust_weight_proof_photo_enable.isEnabled() && (!this.isRebooking || this.cargoInputForm.isAllowUseProof);
  }

  get form() {
    return CargoController.form;
  }

  get totalVolumeWeights() {
    return CargoController.totalVolumeWeights;
  }

  get totalNewCalculationGrossWeights() {
    return CargoController.totalNewCalculationGrossWeights;
  }

  get totalNewCalculationVolumeWeights() {
    return CargoController.totalNewCalculationVolumeWeights;
  }
  get totalNewCalculationVolumeWeightsMaxFlag() {
    const min =
      this.formBooking.cargoType === "plane" && this.formBooking.cargoTypeStatus === "auto" && this.featureHideDimensionKoli
        ? Math.max(1, FlagsPermissionCargo.validation_3lc_cargo_minimum_weight.getValue())
        : 1;

    const volumeWeight = this.parseNumber(this.formBooking.actualTotalCargoVolumeWeight) || this.totalNewCalculationGrossWeights

    if (this.flowBooking === "rtc-process") return Math.max(Math.round(this.totalNewCalculationVolumeWeights), min);
        
    return Math.max(volumeWeight, min);
  }

  get chargeableWeight() {
    return Math.max(
      1,
      this.parseNumber(String(this.totalNewCalculationGrossWeights)),
      this.parseNumber(String(this.totalNewCalculationVolumeWeights))
    );
  }

  get actualChargeableWeight() {
    if (this.isUsePayloadTimbangUlang && !CargoController.isAwbValid) {
      return CargoController.calculateVolumeData.chargeableWeight;
    }

    return Math.max(
      1,
      this.parseNumber(this.form.actualTotalCargoGrossWeight),
      this.parseNumber(this.form.actualTotalCargoVolumeWeight)
    );
  }

get isUploadPhotoAdjustWeightValid() {
    const isError = CargoController.calculateVolumeData.isUploadProofPhoto;
    return isError && this.isValidationAdjustWeightProofPhotoEnable ? this.formBooking.photoProof.filter((photo) => photo).length > 0 : true;
  }

  get isLoadingCalculateVolume() {
    return CargoController.isLoadingCalculateVolume;
  }
}
